import * as React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
} from '@locus-taxy/encore-mui';
import { Close as CloseIcon } from '@locus-taxy/encore-mui/icons';
import { useTranslation } from '@locus-taxy/encore-i18n-toolkit';
import { CustomDetailsRemoteContainer } from './EntityDetails/CustomDetailsRemoteContainer';
import { ErrorBoundary } from './ErrorBoundary';
import { resolveAppConfig } from '../utils/resolveAppConfig';
import { isMfeAppConfig } from '../utils/customApp';
import type { CustomAppConfig } from '../utils/customApp';

interface CustomAppActionModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Handler to close the modal */
  onClose: () => void;
  /** Custom app configuration from the action */
  customApp: CustomAppConfig;
  /** Title for the modal (action label) */
  title?: string;
  /** Client ID */
  clientId: string;
  /** Entity name for config resolution */
  entityName: string;
  /** Entity data to pass to the MFE */
  entityData?: Record<string, unknown>;
}

/**
 * Modal that renders a Module Federation micro-frontend for an action
 * that has a customApp configuration, instead of the default Camunda form.
 */
export function CustomAppActionModal({
  open,
  onClose,
  customApp,
  title,
  clientId,
  entityName,
  entityData,
}: Readonly<CustomAppActionModalProps>) {
  const { t } = useTranslation();
  const resolvedConfig = React.useMemo(() => {
    if (!isMfeAppConfig(customApp)) {
      return null;
    }
    return resolveAppConfig(customApp, entityName);
  }, [customApp, entityName]);

  if (!open || !resolvedConfig) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="h6" component="span">
            {title ?? customApp.name}
          </Typography>
          <IconButton onClick={onClose} size="small" aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 400,
        }}
      >
        <ErrorBoundary
          errorTitle={t('common.customApps.errors.loadFailed')}
          errorMessage={t('common.customApps.errors.loadFailedMessage', { appName: customApp.name })}
        >
          <CustomDetailsRemoteContainer
            config={resolvedConfig}
            clientId={clientId}
            entityName={entityName}
            entityTitle={title ?? customApp.name}
            entityData={entityData ?? {}}
          />
        </ErrorBoundary>
      </DialogContent>
    </Dialog>
  );
}
